import { useState, useCallback, useEffect, useRef } from "react";
import NumberInput from "../NumberInput/NumberInput";
import { useShellIdFn } from "../../VariantB/shellContext";
import {
  IMPERIAL,
  kgToImperial, imperialToKg, cmToImperial, imperialToCm,
} from "./imperialUtils";

type ImperialMode = "height" | "weight";

interface ImperialNumberFieldProps {
  mode: ImperialMode;
  answerId: number;
  validationRule: any;
  questionnaireValidationObject: any;
  onTextChange: (answerId: number, text: string, rule: any, recipeQuestionId: number | null) => void;
  recipeQuestionId: number | null;
  placeholder?: string;
  value: string;
  disabled?: boolean;
  isImperial: boolean;
}

const ImperialNumberField = ({
  mode,
  answerId,
  validationRule,
  questionnaireValidationObject,
  onTextChange,
  recipeQuestionId,
  placeholder,
  value,
  disabled = false,
  isImperial,
}: ImperialNumberFieldProps) => {
  // VariantB renders both shells at once, so these ids would land in the DOM twice.
  // Outside VariantB there is no provider and the ids are returned unchanged.
  const scopeId = useShellIdFn();
  const [fieldA, setFieldA] = useState<string>("");
  const [fieldB, setFieldB] = useState<string>("");
  const [fieldAError, setFieldAError] = useState(false);
  const [fieldBError, setFieldBError] = useState(false);

  const prevImperial = useRef(isImperial);
  const metricValue = Number(value) || 0;

  const isHeight = mode === "height";
  const labelA = isHeight ? IMPERIAL.labels.ft : IMPERIAL.labels.st;
  const labelB = isHeight ? IMPERIAL.labels.in : IMPERIAL.labels.lb;
  const fullLabelA = isHeight ? IMPERIAL.labels.feet : IMPERIAL.labels.stones;
  const fullLabelB = isHeight ? IMPERIAL.labels.inches : IMPERIAL.labels.pounds;

  const minA = isHeight ? (validationRule?.min_ft ?? IMPERIAL.defaults.minFt) : (validationRule?.min_st ?? IMPERIAL.defaults.minSt);
  const maxA = isHeight ? (validationRule?.max_ft ?? IMPERIAL.defaults.maxFt) : (validationRule?.max_st ?? IMPERIAL.defaults.maxSt);
  const minB = isHeight ? (validationRule?.min_in ?? IMPERIAL.defaults.minIn) : (validationRule?.min_lb ?? IMPERIAL.defaults.minLb);
  const maxB = isHeight ? (validationRule?.max_in ?? IMPERIAL.defaults.maxIn) : (validationRule?.max_lb ?? IMPERIAL.defaults.maxLb);

  useEffect(() => {
    if (isImperial === prevImperial.current) return;
    prevImperial.current = isImperial;

    const hasImperialValues = fieldA !== "" || fieldB !== "";

    if (isImperial && !hasImperialValues && metricValue > 0) {
      if (isHeight) {
        const { ft, inches } = cmToImperial(metricValue);
        setFieldA(String(ft));
        setFieldB(String(inches));
      } else {
        const { st, lb } = kgToImperial(metricValue);
        setFieldA(String(st));
        setFieldB(String(lb));
      }
    } else if (!isImperial && metricValue <= 0) {
      const a = Number(fieldA) || 0;
      const b = Number(fieldB) || 0;
      if (a > 0 || b > 0) {
        const metric = isHeight
          ? String(Math.round(imperialToCm(a, b)))
          : String(parseFloat(imperialToKg(a, b).toFixed(1)));
        onTextChange(answerId, metric, validationRule, recipeQuestionId);
      }
    }
  }, [isImperial]);

  const handleImperialChange = useCallback((aVal: string, bVal: string) => {
    const aNum = Number(aVal) || 0;
    const bNum = Number(bVal) || 0;

    const metric = isHeight
      ? String(Math.round(imperialToCm(aNum, bNum)))
      : String(parseFloat(imperialToKg(aNum, bNum).toFixed(1)));

    const isAValid = aVal === "" || (aNum >= minA && aNum <= maxA);
    const isBValid = bVal === "" || (bNum >= minB && bNum <= maxB);

    setFieldAError(!isAValid);
    setFieldBError(!isBValid);

    const rule = isAValid && isBValid ? validationRule : { ...validationRule, min_value: Infinity };
    onTextChange(answerId, metric, rule, recipeQuestionId);
  }, [answerId, validationRule, recipeQuestionId, isHeight, minA, maxA, minB, maxB]);

  if (isImperial) {
    return (
      <div className="unit-toggle-imperial-inputs">
        <div className="unit-toggle-imperial-field">
          <div className="unit-toggle-imperial-field-input">
            <NumberInput
              disabled={disabled}
              maxValue={maxA}
              minValue={minA}
              errorMode={fieldAError}
              placeholder=""
              onChange={(text: string) => {
                setFieldA(text);
                handleImperialChange(text, fieldB);
              }}
              id={scopeId("answer_" + answerId + "_" + labelA)}
              value={fieldA}
            />
            <span className="unit-toggle-imperial-field-suffix">{labelA}</span>
          </div>
          {fieldAError && (
            <span className="unit-toggle-imperial-field-error">
              {fullLabelA} must be between {minA}-{maxA}
            </span>
          )}
        </div>
        <div className="unit-toggle-imperial-field">
          <div className="unit-toggle-imperial-field-input">
            <NumberInput
              disabled={disabled}
              maxValue={maxB}
              minValue={minB}
              errorMode={fieldBError}
              placeholder=""
              onChange={(text: string) => {
                setFieldB(text);
                handleImperialChange(fieldA, text);
              }}
              id={scopeId("answer_" + answerId + "_" + labelB)}
              value={fieldB}
            />
            <span className="unit-toggle-imperial-field-suffix">{labelB}</span>
          </div>
          {fieldBError && (
            <span className="unit-toggle-imperial-field-error">
              {fullLabelB} must be between {minB}-{maxB}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <NumberInput
      disabled={disabled}
      maxValue={validationRule?.max_value}
      minValue={validationRule?.min_value}
      errorMode={questionnaireValidationObject[answerId] === false}
      placeholder={placeholder}
      onChange={(text: string) => onTextChange(answerId, text, validationRule, recipeQuestionId)}
      id={scopeId("answer_" + answerId)}
      value={value}
    />
  );
};

export default ImperialNumberField;
